import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Box, Chip, Stack, CircularProgress, Button, Grid, Paper, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetStudent } from '../../hooks/students/useGetStudent';
import { useTranslation } from 'react-i18next';

const ModelShowStudent = ({ open, onClose, studentId }) => {
    const { i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const theme = useTheme();
    const { student: data, isLoading } = useGetStudent(studentId);
    const s = data?.data;
    const fmt = (v) => v || '-';
    const fmtDate = (v) => v ? new Date(v).toLocaleDateString(isArabic ? 'ar-EG' : 'en-US') : '-';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3, bgcolor: theme.palette.background.card } }}>
            <DialogTitle sx={{ m: 0, px: 3, py: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{isArabic ? 'تفاصيل الطالب' : 'Student Details'}</Typography>
                    <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2, pb: 3, px: 3, direction: isArabic ? 'rtl' : 'ltr' }}>
                {isLoading || !studentId ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={32} /></Box>
                ) : !s ? (
                    <Typography align="center" sx={{ py: 4 }}>{isArabic ? 'لا توجد بيانات' : 'No data'}</Typography>
                ) : (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: theme.palette.divider }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}><Typography variant="h6" fontWeight={600}>{s.full_name}</Typography></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'الكود' : 'Code'} value={fmt(s.code)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'الجنس' : 'Gender'} value={s.gender === 'male' ? (isArabic ? 'ذكر' : 'Male') : (isArabic ? 'أنثى' : 'Female')} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'المدرسة' : 'School'} value={fmt(s.school_name)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'الهاتف' : 'Phone'} value={fmt(s.phone)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'واتساب' : 'WhatsApp'} value={fmt(s.whatsapp)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'رقم البطاقة' : 'National ID'} value={fmt(s.national_id)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'تاريخ الميلاد' : 'Date of Birth'} value={fmtDate(s.date_of_birth)} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'العمر' : 'Age'} value={s.age != null ? s.age : '-'} /></Grid>
                            <Grid item xs={6} sm={4}><Field label={isArabic ? 'الأجزاء المحفوظة' : 'Memorized Parts'} value={`${s.memorized_parts_count ?? 0} / 30`} /></Grid>
                            <Grid item xs={12}><Field label={isArabic ? 'العنوان' : 'Address'} value={fmt(s.address)} /></Grid>
                            {s.guardians?.length > 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">{isArabic ? 'أولياء الأمور' : 'Guardians'}</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                        {s.guardians.map((g) => (
                                            <Chip key={g.id} label={`${g.full_name}${g.relationship ? ` (${g.relationship})` : ''}${g.is_primary ? (isArabic ? ' ★' : ' ★') : ''}`}
                                                size="small" variant="outlined" color={g.is_primary ? 'primary' : 'default'} sx={{ mb: 0.5 }} />
                                        ))}
                                    </Stack>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Chip label={s.is_active ? (isArabic ? 'مفعّل' : 'Active') : (isArabic ? 'موقوف' : 'Inactive')}
                                    size="small" color={s.is_active ? 'success' : 'error'} variant="outlined" />
                            </Grid>
                        </Grid>
                    </Paper>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                <Button onClick={onClose} variant="contained">{isArabic ? 'إغلاق' : 'Close'}</Button>
            </DialogActions>
        </Dialog>
    );
};

const Field = ({ label, value }) => (<><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="body2" fontWeight={500}>{value}</Typography></>);

export default ModelShowStudent;
